import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useIsGooglePayAvailable } from '../../lib/payments/GooglePay/hooks/useIsGooglePayAvailable';
import { useIsApplePayAvailable } from '../../lib/payments/ApplePay/hooks/useIsApplePayAvailable';
import { PaymentInstrumentType } from '../../graphql/types.generated';
import { PaymentInstrument } from './types';
import { applePayPaymentInstrument, digitalWalletInstrumentsTitle, googlePayPaymentInstrument } from './constants';

export interface IUseGetOnDevicePaymentInstrumentsParams {
  skip?: boolean;
}

/**
 * Return device (OS) digital wallets if available
 * iOS: Apple Pay, Google Pay
 * Android: Google Pay
 */
export const useGetOnDevicePaymentInstruments = (params: IUseGetOnDevicePaymentInstrumentsParams = {}) => {
  const { skip = false } = params;
  const intl = useIntl();

  const googlePay = useIsGooglePayAvailable();
  const applePay = useIsApplePayAvailable();

  return useMemo(() => {
    if (skip) {
      return {
        loading: false,
        paymentInstruments: [],
      };
    }

    const paymentInstruments: PaymentInstrument[] = [];
    if (!applePay.loading && applePay.available) {
      paymentInstruments.push({
        ...applePayPaymentInstrument,
        displayName: intl.formatMessage(digitalWalletInstrumentsTitle[PaymentInstrumentType.ApplePay]),
      });
    }
    if (!googlePay.loading && googlePay.available) {
      paymentInstruments.push({
        ...googlePayPaymentInstrument,
        displayName: intl.formatMessage(digitalWalletInstrumentsTitle[PaymentInstrumentType.GooglePay]),
      });
    }
    return {
      loading: applePay.loading || googlePay.loading,
      paymentInstruments,
    };
  }, [skip, applePay.available, applePay.loading, googlePay.available, googlePay.loading, intl]);
};
